import type { Joi } from '@ksyos/koi';
import { Koi } from '@ksyos/koi';
import { OsasComplaintBurden, OsasTrialPeriod, YesOrNoOrUnknown } from '@common/enums/';
import { MraAbcdScore } from '@common/enums/mraAbcdScore';
import { OsasPapType } from '@common/enums/osasPapType';
import { ComorbiditiesChoice } from '../../enums/comorbiditiesChoice';
import { ConsultationPlace } from '../../enums/consultationPlace';
import { OsasQuestionnaireName } from '../../enums/osasQuestionnaireName';
import { Retrognathism } from '../../enums/physicalExamination/retrognathism';
import { TongueSize } from '../../enums/physicalExamination/tongueSize';
import { TonsilSize } from '../../enums/physicalExamination/tonsilSize';
import { SleepQuestionnairesValidationType } from '../../enums/sleepQuestionnairesValidationType';
import { SomnologyOrganizationType } from '../../enums/somnologyOrganizationType';
import { asset } from '../assets';
import { patientContactInfoDraft, patientContactInfoFinal } from '../patientContactInfo';
import { emailSchema } from '../schemas/emailSchema';
import { ggzbTherCheckedTreatments } from './mentalHealth';

const relevanteKlachtenCustomControlDataNsi = Koi.object({
    andersText: Koi.string().max(255).allow(null),
    meer_dan_5_ademstops_per_uur: Koi.boolean(),
    niet_verfrissende_slaap: Koi.boolean(),
    stokkende_ademhaling_tijdens_de_slaap: Koi.boolean(),
    veel_snurken: Koi.boolean(),
    verminderde_concentratie: Koi.boolean(),
    vermoeidheid_overdag: Koi.boolean(),
});

const comorbiditeitenCustomControlDataKsyosMedicalCenter = Koi.object({
    anders_checked: Koi.boolean().allow(null),
    anders_text: Koi.string().max(255).allow(null),
    burn_out_of_depressieve_klachten: Koi.boolean(),
    cardiale_problematiek: Koi.boolean(),
    diabetes_mellitus: Koi.boolean(),
    hoge_bloeddruk: Koi.boolean(),
});

const comorbiditeitenCustomControlDataNsi = Koi.object({
    anders_checked: Koi.boolean().allow(null),
    anders_text: Koi.valid(null),
    burn_out_of_depressieve_klachten: Koi.valid(false),
    cardiale_problematiek: Koi.valid(false),
    diabetes_mellitus: Koi.valid(false),
    hoge_bloeddruk: Koi.valid(false),
});

export enum SleepRecorderType {
    nightowl = 'nightowl',
    nightowlReusable = 'NightOwl Reusable',
    watchpat = 'watchpat',
    watchpat300 = 'WatchPAT 300',
    watchpatOne = 'WatchPAT ONE',
}

export enum SendSleepRecorderOption {
    giveToThePatient = 'giveToThePatient',
    giveToThePatientKsyos = 'giveToThePatientKsyos',
    sendByKsyos = 'sendByKsyos',
    sendByNsi = 'sendByNsi',
}

export const watchpatSleepRecorderTypes = [
    SleepRecorderType.watchpat,
    SleepRecorderType.watchpat300,
    SleepRecorderType.watchpatOne,
];

export const nightOwlSleepRecorderTypes = [SleepRecorderType.nightowl, SleepRecorderType.nightowlReusable];

export const osasc1V1DraftDiagnosticReport = Koi.object().keys({
    ademstops_remark: Koi.string().max(255).allow(null),
    ademstops_value: Koi.string().max(255).allow(null),
    anamnese_overig: Koi.string().max(2000).allow(null),
    bmiMeasuredBy: Koi.when('somnologyOrganizationType', {
        is: SomnologyOrganizationType.ksyosMedicalCenter,
        otherwise: Koi.valid(null),
        then: Koi.string().max(255).allow(null),
    }),
    bmiMeasuredDate: Koi.when('somnologyOrganizationType', {
        is: SomnologyOrganizationType.ksyosMedicalCenter,
        otherwise: Koi.valid(null),
        then: Koi.string().max(255).allow(null),
    }),
    comorbiditeiten: Koi.when('somnologyOrganizationType', {
        is: SomnologyOrganizationType.ksyosMedicalCenter,
        otherwise: comorbiditeitenCustomControlDataNsi,
        then: comorbiditeitenCustomControlDataKsyosMedicalCenter,
    }),
    comorbiditeitenChoice: Koi.when('somnologyOrganizationType', {
        is: SomnologyOrganizationType.ksyosMedicalCenter,
        otherwise: Koi.valid(null),
        then: Koi.koi().enum(ComorbiditiesChoice).allow(null),
    }),
    concentratieverlies_remark: Koi.string().max(255).allow(null),
    concentratieverlies_value: Koi.string().max(255).allow(null),
    consultationPlace: Koi.when('somnologyOrganizationType', {
        is: SomnologyOrganizationType.ksyosMedicalCenter,
        otherwise: Koi.valid(null),
        then: Koi.koi().enum(ConsultationPlace).allow(null),
    }),
    dentitie_goed_gesaneerd_remark: Koi.string().max(255).allow(null),
    dentitie_goed_gesaneerd_value: Koi.string().max(255).allow(null),
    ervaring_te_stikken_tijdens_slaap_remark: Koi.string().max(255).allow(null),
    ervaring_te_stikken_tijdens_slaap_value: Koi.string().max(255).allow(null),
    gebitsprothese_value: Koi.string().max(255).allow(null),
    geheugenproblemen_remark: Koi.string().max(255).allow(null),
    geheugenproblemen_value: Koi.string().max(255).allow(null),
    gewicht: Koi.when('somnologyOrganizationType', {
        is: SomnologyOrganizationType.ksyosMedicalCenter,
        otherwise: Koi.valid(null),
        then: Koi.number().min(0).max(10000).allow(null),
    }),
    herhaald_wakker_schrikken_remark: Koi.string().max(255).allow(null),
    herhaald_wakker_schrikken_value: Koi.string().max(255).allow(null),
    humeur_kort_lontje_depressieve_gevoelens_remark: Koi.string().max(255).allow(null),
    humeur_kort_lontje_depressieve_gevoelens_value: Koi.string().max(255).allow(null),
    lengte: Koi.when('somnologyOrganizationType', {
        is: SomnologyOrganizationType.ksyosMedicalCenter,
        otherwise: Koi.valid(null),
        then: Koi.number().min(0).max(10000).allow(null),
    }),
    niet_verfrissende_slaap_remark: Koi.string().max(255).allow(null),
    niet_verfrissende_slaap_value: Koi.string().max(255).allow(null),
    overmatige_slaperigheid_overdag_remark: Koi.string().max(255).allow(null),
    overmatige_slaperigheid_overdag_value: Koi.string().max(255).allow(null),
    poligraphyExam: Koi.string().max(255).allow(null),
    retrognathism: Koi.koi().enum(Retrognathism).allow(null),
    snurken_remark: Koi.string().max(255).allow(null),
    snurken_value: Koi.string().max(255).allow(null),
    somnologyOrganizationType: Koi.koi().enum(SomnologyOrganizationType).allow(null),
    stokkende_ademhaling_remark: Koi.string().max(255).allow(null),
    stokkende_ademhaling_value: Koi.string().max(255).allow(null),
    tongueSize: Koi.koi().enum(TongueSize).allow(null),
    tonsilSize: Koi.koi().enum(TonsilSize).allow(null),
    vermoeidheid_overdag_remark: Koi.string().max(255).allow(null),
    vermoeidheid_overdag_value: Koi.string().max(255).allow(null),
});

export const osasc1V1FinalDiagnosticReport = Koi.object().keys({
    ademstops_remark: Koi.string().max(1000).allow(null),
    ademstops_value: Koi.string().valid('Ja', 'Nee', 'Onbekend'),
    anamnese_overig: Koi.string().max(2000).allow(null),
    bmiMeasuredBy: Koi.when('somnologyOrganizationType', {
        is: SomnologyOrganizationType.ksyosMedicalCenter,
        otherwise: Koi.valid(null),
        then: Koi.string().max(255).allow(null),
    }),
    bmiMeasuredDate: Koi.when('somnologyOrganizationType', {
        is: SomnologyOrganizationType.ksyosMedicalCenter,
        otherwise: Koi.valid(null),
        then: Koi.string().max(255).allow(null),
    }),
    comorbiditeiten: Koi.when('somnologyOrganizationType', {
        is: SomnologyOrganizationType.ksyosMedicalCenter,
        otherwise: comorbiditeitenCustomControlDataNsi,
        then: comorbiditeitenCustomControlDataKsyosMedicalCenter,
    }),
    comorbiditeitenChoice: Koi.when('somnologyOrganizationType', {
        is: SomnologyOrganizationType.ksyosMedicalCenter,
        otherwise: Koi.valid(null),
        then: Koi.koi().enum(ComorbiditiesChoice),
    }),
    concentratieverlies_remark: Koi.string().max(1000).allow(null),
    concentratieverlies_value: Koi.string().valid('Ja', 'Nee', 'Onbekend'),
    consultationPlace: Koi.when('somnologyOrganizationType', {
        is: SomnologyOrganizationType.ksyosMedicalCenter,
        otherwise: Koi.valid(null),
        then: Koi.koi().enum(ConsultationPlace),
    }),
    dentitie_goed_gesaneerd_remark: Koi.string().max(255).allow(null),
    dentitie_goed_gesaneerd_value: Koi.when('somnologyOrganizationType', {
        is: SomnologyOrganizationType.ksyosMedicalCenter,
        otherwise: Koi.valid(null),
        then: Koi.string().valid('Ja', 'Nee', 'Onbekend'),
    }),
    ervaring_te_stikken_tijdens_slaap_remark: Koi.when('somnologyOrganizationType', {
        is: SomnologyOrganizationType.ksyosMedicalCenter,
        otherwise: Koi.valid(null),
        then: Koi.string().max(1000).allow(null),
    }),
    ervaring_te_stikken_tijdens_slaap_value: Koi.when('somnologyOrganizationType', {
        is: SomnologyOrganizationType.ksyosMedicalCenter,
        otherwise: Koi.valid(null),
        then: Koi.string().valid('Ja', 'Nee', 'Onbekend'),
    }),
    gebitsprothese_value: Koi.when('somnologyOrganizationType', {
        is: SomnologyOrganizationType.ksyosMedicalCenter,
        otherwise: Koi.valid(null),
        then: Koi.string().valid('Ja', 'Nee', 'Onbekend'),
    }),
    geheugenproblemen_remark: Koi.when('somnologyOrganizationType', {
        is: SomnologyOrganizationType.ksyosMedicalCenter,
        otherwise: Koi.string().max(1000).allow(null),
        then: Koi.valid(null),
    }),
    geheugenproblemen_value: Koi.when('somnologyOrganizationType', {
        is: SomnologyOrganizationType.ksyosMedicalCenter,
        otherwise: Koi.string().valid('Ja', 'Nee', 'Onbekend'),
        then: Koi.valid(null),
    }),
    gewicht: Koi.when('somnologyOrganizationType', {
        is: SomnologyOrganizationType.ksyosMedicalCenter,
        otherwise: Koi.valid(null),
        then: Koi.number().min(30).max(300),
    }),
    herhaald_wakker_schrikken_remark: Koi.when('somnologyOrganizationType', {
        is: SomnologyOrganizationType.ksyosMedicalCenter,
        otherwise: Koi.string().max(1000).allow(null),
        then: Koi.valid(null),
    }),
    herhaald_wakker_schrikken_value: Koi.when('somnologyOrganizationType', {
        is: SomnologyOrganizationType.ksyosMedicalCenter,
        otherwise: Koi.string().valid('Ja', 'Nee', 'Onbekend'),
        then: Koi.valid(null),
    }),
    humeur_kort_lontje_depressieve_gevoelens_remark: Koi.when('somnologyOrganizationType', {
        is: SomnologyOrganizationType.ksyosMedicalCenter,
        otherwise: Koi.string().max(1000).allow(null),
        then: Koi.valid(null),
    }),
    humeur_kort_lontje_depressieve_gevoelens_value: Koi.when('somnologyOrganizationType', {
        is: SomnologyOrganizationType.ksyosMedicalCenter,
        otherwise: Koi.string().valid('Ja', 'Nee', 'Onbekend'),
        then: Koi.valid(null),
    }),
    lengte: Koi.when('somnologyOrganizationType', {
        is: SomnologyOrganizationType.ksyosMedicalCenter,
        otherwise: Koi.valid(null),
        then: Koi.number().min(100).max(230),
    }),
    niet_verfrissende_slaap_remark: Koi.string().max(1000).allow(null),
    niet_verfrissende_slaap_value: Koi.string().valid('Ja', 'Nee', 'Onbekend'),
    overmatige_slaperigheid_overdag_remark: Koi.string().max(1000).allow(null),
    overmatige_slaperigheid_overdag_value: Koi.string().valid('Ja', 'Nee', 'Onbekend'),
    poligraphyExam: Koi.string()
        .max(1000)
        .disallow(null)
        .when('somnologyOrganizationType', { is: SomnologyOrganizationType.ksyosMedicalCenter, then: Koi.allow(null) })
        .when('overmatige_slaperigheid_overdag_value', { is: 'Ja', then: Koi.allow(null) })
        .when('stokkende_ademhaling_value', { is: 'Ja', then: Koi.allow(null) })
        .when('herhaald_wakker_schrikken_value', { is: 'Ja', then: Koi.allow(null) })
        .when('niet_verfrissende_slaap_value', { is: 'Ja', then: Koi.allow(null) })
        .when('vermoeidheid_overdag_value', { is: 'Ja', then: Koi.allow(null) })
        .when('concentratieverlies_value', { is: 'Ja', then: Koi.allow(null) })
        .when('snurken_value', { is: 'Ja', then: Koi.allow(null) })
        .when('ademstops_value', { is: 'Ja', then: Koi.allow(null) })
        .when('humeur_kort_lontje_depressieve_gevoelens_value', { is: 'Ja', then: Koi.allow(null) })
        .when('geheugenproblemen_value', { is: 'Ja', then: Koi.allow(null) }),
    retrognathism: Koi.when('somnologyOrganizationType', {
        is: SomnologyOrganizationType.ksyosMedicalCenter,
        otherwise: Koi.valid(null),
        then: Koi.koi().enum(Retrognathism).allow(null),
    }),
    snurken_remark: Koi.string().max(1000).allow(null),
    snurken_value: Koi.string().valid('Ja', 'Nee', 'Onbekend'),
    somnologyOrganizationType: Koi.koi().enum(SomnologyOrganizationType),
    stokkende_ademhaling_remark: Koi.when('somnologyOrganizationType', {
        is: SomnologyOrganizationType.ksyosMedicalCenter,
        otherwise: Koi.string().max(1000).allow(null),
        then: Koi.valid(null),
    }),
    stokkende_ademhaling_value: Koi.when('somnologyOrganizationType', {
        is: SomnologyOrganizationType.ksyosMedicalCenter,
        otherwise: Koi.string().valid('Ja', 'Nee', 'Onbekend'),
        then: Koi.valid(null),
    }),
    tongueSize: Koi.koi().enum(TongueSize).allow(null),
    tonsilSize: Koi.koi().enum(TonsilSize).allow(null),
    vermoeidheid_overdag_remark: Koi.string().max(1000).allow(null),
    vermoeidheid_overdag_value: Koi.string().valid('Ja', 'Nee', 'Onbekend'),
});

const addressValidationMandatory = Koi.object().keys({
    city: Koi.string().max(255),
    country: Koi.string().max(255),
    houseNumber: Koi.string().max(255),
    houseNumberSuffix: Koi.string().max(255).allow(null),
    street: Koi.string().max(255),
    zip: Koi.when('country', {
        is: 'Nederland',
        otherwise: Koi.string().max(255).allow(null),
        then: Koi.string().zipCodeNL(),
    }),
});

const addressValidationOptional = Koi.object().keys({
    city: Koi.string().max(255).allow(null),
    country: Koi.string().max(255).allow(null),
    houseNumber: Koi.string().max(255).allow(null),
    houseNumberSuffix: Koi.string().max(255).allow(null),
    street: Koi.string().max(255).allow(null),
    zip: Koi.string()
        .allow(null)
        .when('country', {
            is: 'Nederland',
            otherwise: Koi.string().max(255),
            then: Koi.string().zipCodeNL(),
        }),
});

export const osasvV1DraftReferralSmall = Koi.object().keys({
    consult: Koi.when('somnologyOrganizationType', {
        is: SomnologyOrganizationType.ksyosMedicalCenter,
        otherwise: Koi.valid(null),
        then: osasc1V1DraftDiagnosticReport,
    }),
    contactgegevens_patient: patientContactInfoDraft.concat(addressValidationOptional),
    hoofdklacht: Koi.when('somnologyOrganizationType', {
        is: SomnologyOrganizationType.ksyosMedicalCenter,
        otherwise: Koi.valid(null),
        then: Koi.string().max(255).allow(null),
    }),
    osasReferralPermission: Koi.object().keys({
        patientPermission: Koi.boolean(),
        patientQuestionnaireEmail: Koi.boolean(),
    }),
    patientHasSmartphone: Koi.boolean().allow(null),
    relevante_klachten: Koi.when('somnologyOrganizationType', {
        is: SomnologyOrganizationType.ksyosMedicalCenter,
        otherwise: relevanteKlachtenCustomControlDataNsi,
        then: Koi.valid(null),
    }),
    slaapapneu_in_de_1e_graad: Koi.string().valid('Ja', 'Nee', 'Onbekend').allow(null),
    slaaprecorderVerzenden: Koi.when('somnologyOrganizationType', {
        is: SomnologyOrganizationType.nsi,
        otherwise: Koi.valid(SendSleepRecorderOption.sendByKsyos).allow(null),
        then: Koi.string()
            .valid(SendSleepRecorderOption.sendByNsi, SendSleepRecorderOption.giveToThePatient)
            .allow(null),
    }),
    somnologyOrganizationType: Koi.koi().enum(SomnologyOrganizationType),
    typeSlaaprecorder: Koi.when('somnologyOrganizationType', {
        is: SomnologyOrganizationType.ksyosMedicalCenter,
        otherwise: Koi.valid(null),
        then: Koi.when('slaaprecorderVerzenden', {
            switch: [
                {
                    is: SendSleepRecorderOption.sendByKsyos,
                    then: Koi.valid(
                        SleepRecorderType.watchpat300,
                        SleepRecorderType.watchpatOne,
                        SleepRecorderType.nightowl
                    ).allow(null),
                },
                { is: null, then: Koi.valid(null) },
            ],
        }),
    }),
    verwijsreden: Koi.string().max(255).allow(null),
    voorgeschiedenis: Koi.string().max(10000000).allow(null),
});

export const osasvV1FinalReferralSmall = Koi.object().keys({
    consult: Koi.when('somnologyOrganizationType', {
        is: SomnologyOrganizationType.ksyosMedicalCenter,
        otherwise: Koi.valid(null),
        then: osasc1V1FinalDiagnosticReport,
    }),
    contactgegevens_patient: Koi.when('somnologyOrganizationType', {
        is: SomnologyOrganizationType.nsi,
        otherwise: patientContactInfoFinal.concat(addressValidationMandatory),
        then: patientContactInfoFinal.concat(addressValidationOptional),
    }),
    hoofdklacht: Koi.when('somnologyOrganizationType', {
        is: SomnologyOrganizationType.ksyosMedicalCenter,
        otherwise: Koi.valid(null),
        then: Koi.string().max(255),
    }),
    osasReferralPermission: Koi.object().keys({
        patientPermission: Koi.boolean(),
        patientQuestionnaireEmail: Koi.boolean(),
    }),
    patientHasSmartphone: Koi.boolean().allow(null),
    relevante_klachten: Koi.when('somnologyOrganizationType', {
        is: SomnologyOrganizationType.ksyosMedicalCenter,
        otherwise: relevanteKlachtenCustomControlDataNsi,
        then: Koi.valid(null),
    }),
    slaapapneu_in_de_1e_graad: Koi.string().valid('Ja', 'Nee', 'Onbekend').allow(null),
    slaaprecorderVerzenden: Koi.when('somnologyOrganizationType', {
        is: SomnologyOrganizationType.nsi,
        otherwise: Koi.valid(SendSleepRecorderOption.sendByKsyos),
        then: Koi.string().valid(SendSleepRecorderOption.sendByNsi, SendSleepRecorderOption.giveToThePatient),
    }),
    somnologyOrganizationType: Koi.koi().enum(SomnologyOrganizationType),
    typeSlaaprecorder: Koi.when('somnologyOrganizationType', {
        is: SomnologyOrganizationType.ksyosMedicalCenter,
        otherwise: Koi.valid(null),
        then: Koi.when('slaaprecorderVerzenden', {
            switch: [
                {
                    is: SendSleepRecorderOption.sendByKsyos,
                    then: Koi.valid(
                        SleepRecorderType.watchpat300,
                        SleepRecorderType.watchpatOne,
                        SleepRecorderType.nightowl
                    ),
                },
                { is: null, then: Koi.string().required() },
            ],
        }),
    }),
    verwijsreden: Koi.string().max(255),
    voorgeschiedenis: Koi.when('somnologyOrganizationType', {
        is: SomnologyOrganizationType.nsi,
        otherwise: Koi.string().max(10000000),
        then: Koi.string().max(10000000).allow(null),
    }),
});

export const osasc2V1DraftDiagnosticReport = Koi.object().keys({
    dateConsult: Koi.string().max(10).allow(null),
    policy: Koi.string().max(255).allow(null),
    remarks: Koi.string().max(1000).allow(null),
    workingDiagnosis: Koi.string().max(255).allow(null),
});

export const osasc2V1FinalDiagnosticReport = Koi.object().keys({
    dateConsult: Koi.string().max(10),
    policy: Koi.string().max(255),
    remarks: Koi.string().max(1000),
    workingDiagnosis: Koi.string().max(255),
});

export const positieafhankelijkOptions = ['Ja', 'Nee', 'NVT'];
export const mateVanOsaOptions = ['Licht', 'Matig', 'Ernstig'];
export const mateVanZiektelastOptions = ['Geen', 'Licht', 'Matig', 'Ernstig'];
export const rijbevoegdheidBesprokenOptions = ['Ja', 'Nee', 'NVT'];

/**
 * @deprecated Please use CpapOption src/common/enums/CpapOption.ts instead
 */
export enum CpapOption {
    custom = 'Aangepast',
    standard = 'Standaard',
}

/**
 * @deprecated Please use CpapClassOption src/common/enums/CpapClassOption.ts instead
 */
export enum CpapBeleidOption {
    clearDiagnosis = 'Niet complexe CPAP-behandeling. Diagnose is duidelijk',
    clearDiagnosisWithUnpredictableEffectOfTreatment = 'Niet complexe CPAP-behandeling, diagnose is duidelijk: effect van behandeling is niet te voorspellen',
    complexTreatment = 'Complexe CPAP-behandeling',
    diagnosticCourse = 'Diagnostisch traject met CPAP of zgn. “Lakmoesproef”',
}

/**
 * @deprecated Please use SptOption src/common/enums/SptOption.ts instead
 */
export enum SptOption {
    A = 'A: AHI < 15',
    B = 'B: AHI > 15',
}

const osasc2V2KsyosMedicalCenterFinalDiagnosticReport = Koi.object().keys({
    aanvullendLichamelijkOnderzoek: Koi.string().max(1000).allow(null),
    aanvullendeAnamnese: Koi.string().max(3000).allow(null),
    adviesAanDeHuisarts: Koi.when('osa', {
        is: true,
        otherwise: Koi.string().max(1000),
        then: Koi.string().max(1000).allow(null),
    }),
    afwijkendHartritme: Koi.boolean(),
    afwijkendHartritmeRemarks: Koi.when('afwijkendHartritme', {
        is: true,
        otherwise: Koi.valid(null),
        then: Koi.string().max(1000).allow(null),
    }),
    andereComorbiditeiten: Koi.when('osa', {
        is: true,
        otherwise: Koi.valid(null),
        then: Koi.boolean(),
    }),
    andereComorbiditeitenRemarks: Koi.when('andereComorbiditeiten', {
        is: true,
        otherwise: Koi.valid(null),
        then: Koi.string().max(1000).allow(null),
    }),
    behandeling1eLijnRemark: Koi.string().max(1000).allow(null),
    beleid: Koi.object().keys({
        adviesLifestyleVerandering: Koi.boolean(),
        adviesLifestyleVeranderingRemarks: Koi.string().max(1000).allow(null),
        beleidAndersText: Koi.string().max(1000).allow(null),
        cpap: Koi.boolean(),
        cpapBeleidOption: Koi.when('cpap', {
            is: true,
            otherwise: Koi.valid(null),
            then: Koi.koi().enum(CpapBeleidOption),
        }),
        cpapBeleidOptionRemark: Koi.when('cpapBeleidOption', {
            is: null,
            otherwise: Koi.string().max(1000).allow(null, ''),
            then: Koi.valid(null),
        }),
        cpapOption: Koi.when('cpap', {
            is: true,
            otherwise: Koi.valid(null),
            then: Koi.koi().enum(CpapOption),
        }),
        cpapOptionRemark: Koi.when('cpapOption', {
            is: CpapOption.custom,
            otherwise: Koi.valid(null),
            then: Koi.string().max(1000).allow(null, ''),
        }),
        doorverwijzen: Koi.boolean(),
        doorverwijzenRemarks: Koi.string().max(1000).allow(null),
        mra: Koi.boolean(),
        mraAbcdScore: Koi.when('mra', {
            is: true,
            otherwise: Koi.valid(null),
            then: Koi.koi().enum(MraAbcdScore),
        }),
        mraAbcdScoreRemark: Koi.when('mraAbcdScore', {
            is: null,
            otherwise: Koi.string().max(1000).allow(null, ''),
            then: Koi.valid(null),
        }),
        spt: Koi.boolean(),
        sptOption: Koi.when('spt', {
            is: true,
            otherwise: Koi.valid(null),
            then: Koi.koi().enum(SptOption),
        }),
        sptOptionRemark: Koi.when('sptOption', {
            is: null,
            otherwise: Koi.string().max(1000).allow(null, ''),
            then: Koi.valid(null),
        }),
    }),
    bmiCategorie: Koi.string().max(1000).allow(null),
    checkedTreatments: ggzbTherCheckedTreatments,
    comorbiditeitenVolgensHuisarts: comorbiditeitenCustomControlDataKsyosMedicalCenter.allow(null),
    dateConsult: Koi.koi().date(),
    emailAddress: emailSchema.allow(null),
    gemiddeldeAhi: Koi.string().max(1000),
    gemiddeldeOdi: Koi.string().max(1000),
    gemiddeldeSaturatie: Koi.string().max(100),
    inefficienteSlaaphygiene: Koi.boolean(),
    inefficienteSlaaphygieneRemarks: Koi.when('inefficienteSlaaphygiene', {
        is: true,
        otherwise: Koi.valid(null),
        then: Koi.string().max(1000).allow(null),
    }),
    laagstGemetenSaturatie: Koi.string().max(100),
    mateVanOsa: Koi.when('osa', {
        is: true,
        otherwise: Koi.valid(null),
        then: Koi.string().valid(...mateVanOsaOptions),
    }),
    mateVanZiektelast: Koi.when('osa', {
        is: true,
        otherwise: Koi.valid(null),
        then: Koi.string().valid(...mateVanZiektelastOptions),
    }),
    odi4: Koi.string().max(100),
    osa: Koi.boolean(),
    osasq_resend_date: Koi.koi().date().allow(null),
    osasq_resend_week_dif: Koi.number().min(1).max(100).allow(null),
    overigeRelevanteBevindingenAndersText: Koi.string().max(1000).allow(null),
    positieafhankelijk: Koi.string().valid(...positieafhankelijkOptions),
    positieafhankelijkRemarks: Koi.when('positieafhankelijk', {
        is: 'Ja',
        otherwise: Koi.valid(null),
        then: Koi.string().max(1000).allow(null),
    }),
    relevanteHypoxemie: Koi.boolean(),
    relevanteHypoxemieRemarks: Koi.when('relevanteHypoxemie', {
        is: true,
        otherwise: Koi.valid(null),
        then: Koi.string().max(1000).allow(null),
    }),
    rijbevoegdheidBesproken: Koi.when('osa', {
        is: true,
        otherwise: Koi.valid(null),
        then: Koi.string().valid(...rijbevoegdheidBesprokenOptions),
    }),

    rijbevoegdheidBesprokenRemark: Koi.string().max(1000).allow(null),
    slechteSlaapefficientie: Koi.boolean(),
    veelBewegingen: Koi.boolean(),
    vervolgAfspraakMetSpecialistAndersText: Koi.when('osa', {
        is: true,
        otherwise: Koi.valid(null),
        then: Koi.string().max(1000).allow(null),
    }),
    volgensProtocol: Koi.boolean(),
});

export const osasc2V2KsyosMedicalCenterDraftDiagnosticReport = osasc2V2KsyosMedicalCenterFinalDiagnosticReport.concat(
    Koi.object().keys({
        adviesAanDeHuisarts: Koi.string().max(1000).allow(null, ''),
        andereComorbiditeiten: Koi.boolean().allow(null),
        behandeling1eLijnRemark: Koi.string().max(1000).allow(null),
        beleid: Koi.object().keys({
            cpapBeleidOption: Koi.when('cpap', {
                is: true,
                otherwise: Koi.valid(null),
                then: Koi.koi().enum(CpapBeleidOption).allow(null),
            }),
            cpapOption: Koi.when('cpap', {
                is: true,
                otherwise: Koi.valid(null),
                then: Koi.koi().enum(CpapOption).allow(null),
            }),
            mraAbcdScore: Koi.when('mra', {
                is: true,
                otherwise: Koi.valid(null),
                then: Koi.koi().enum(MraAbcdScore).allow(null),
            }),
            sptOption: Koi.when('spt', {
                is: true,
                otherwise: Koi.valid(null),
                then: Koi.koi().enum(SptOption).allow(null),
            }),
        }),
        dateConsult: Koi.koi().date().allow(null),
        gemiddeldeAhi: Koi.string().max(1000).allow(null, ''),
        gemiddeldeOdi: Koi.string().max(1000).allow(null, ''),
        gemiddeldeSaturatie: Koi.string().max(100).allow(null, ''),
        laagstGemetenSaturatie: Koi.string().max(100).allow(null, ''),
        mateVanOsa: Koi.string()
            .valid(...mateVanOsaOptions)
            .allow(null),
        mateVanZiektelast: Koi.string()
            .valid(...mateVanZiektelastOptions)
            .allow(null),
        odi4: Koi.string().max(100).allow(null, ''),
        osa: Koi.boolean().allow(null),
        osasq_resend_date: Koi.koi().date().allow(null),
        osasq_resend_week_dif: Koi.number().min(1).max(100).allow(null),
        positieafhankelijk: Koi.string()
            .valid(...positieafhankelijkOptions)
            .allow(null),
        relevanteHypoxemie: Koi.boolean().allow(null),
        rijbevoegdheidBesproken: Koi.string()
            .valid(...rijbevoegdheidBesprokenOptions)
            .allow(null),
    })
);

export const osasc2V2FinalDiagnosticReport = Koi.object().keys({
    fields: Koi.when('somnologyOrganizationType', {
        is: SomnologyOrganizationType.ksyosMedicalCenter,
        otherwise: osasc2V1FinalDiagnosticReport,
        then: osasc2V2KsyosMedicalCenterFinalDiagnosticReport,
    }),
    somnologyOrganizationType: Koi.koi().enum(SomnologyOrganizationType),
});

export const osasc2V2DraftDiagnosticReport = Koi.object().keys({
    fields: Koi.when('somnologyOrganizationType', {
        is: SomnologyOrganizationType.ksyosMedicalCenter,
        otherwise: osasc2V1DraftDiagnosticReport,
        then: osasc2V2KsyosMedicalCenterDraftDiagnosticReport,
    }),
    somnologyOrganizationType: Koi.koi().enum(SomnologyOrganizationType),
});

export const osasc1V1Remark = Koi.string()
    .max(1000)
    .disallow(null)
    .when('customControlData.somnologyOrganizationType', {
        is: SomnologyOrganizationType.ksyosMedicalCenter,
        then: Koi.allow(null),
    })
    .when('customControlData.overmatige_slaperigheid_overdag_value', { is: 'Ja', then: Koi.allow(null) })
    .when('customControlData.stokkende_ademhaling_value', { is: 'Ja', then: Koi.allow(null) })
    .when('customControlData.herhaald_wakker_schrikken_value', { is: 'Ja', then: Koi.allow(null) })
    .when('customControlData.niet_verfrissende_slaap_value', { is: 'Ja', then: Koi.allow(null) })
    .when('customControlData.vermoeidheid_overdag_value', { is: 'Ja', then: Koi.allow(null) })
    .when('customControlData.concentratieverlies_value', { is: 'Ja', then: Koi.allow(null) })
    .when('customControlData.snurken_value', { is: 'Ja', then: Koi.allow(null) })
    .when('customControlData.ademstops_value', { is: 'Ja', then: Koi.allow(null) })
    .when('customControlData.humeur_kort_lontje_depressieve_gevoelens_value', { is: 'Ja', then: Koi.allow(null) })
    .when('customControlData.geheugenproblemen_value', { is: 'Ja', then: Koi.allow(null) });

export const osasc2external = {
    schema: Koi.object()
        .keys({
            beleid: Koi.string().max(10485760),
            bevindingen: Koi.string().max(10000),
            consultType: Koi.string().valid('telefonisch', 'video', 'op_locatie'),
            datumConsult: Koi.koi().date(),
            diagnose: Koi.string().max(1000), // NSI does not know the max size of this field, this limits it to 10MB
            toelichting: Koi.string().max(10000).optional().allow(null, ''),
        })
        .options({ convert: false, presence: 'required' }),
};

export const akkoordVerklaringOptions = ['Therapie stoppen', 'Therapie akkoord verklaren'];

export const osasendV1FinalRequest = Koi.object().keys({
    akkoordVerklaring: Koi.string().valid(...akkoordVerklaringOptions),
    toelichting: Koi.string().max(10000).allow(null),
});

export const osasendV1DraftRequest = osasendV1FinalRequest.concat(
    Koi.object({
        akkoordVerklaring: Koi.any().allow(null),
    })
);

export const osasvcV1FinalRequest = Koi.object().keys({
    anamnese: Koi.string().max(10000).allow(null),
    beleid: Koi.string().max(10000).allow(null),
    dateContact: Koi.koi().date(),
    diagnose: Koi.string().max(10000).allow(null),
    onderzoek: Koi.string().max(10000).allow(null),
});

export const osasvcV1DraftRequest = osasvcV1FinalRequest.concat(
    Koi.object({
        dateContact: Koi.koi().date().allow(null),
    })
);

export enum OsasvcV2TherapieNamelijkOption {
    biPap = 'BiPAP',
    cpap = 'CPAP',
    mra = 'MRA',
    spt = 'SPT',
}

export enum OsasvcV2CpapOption {
    custom = 'Aangepaste instellingen',
    standard = 'Standaard instellingen',
}

export enum OsasvcV2CTherapieGestartOption {
    continue = 'Continuëren',
    stop = 'Stoppen',
}

function getNieuwSlaaponderzoekConditionalValidationForField(validation: Joi.SchemaLike, isBoolean?: boolean) {
    return Koi.when('nieuw_slaaponderzoek', {
        is: true,
        otherwise: isBoolean ? Koi.valid(false) : Koi.valid(null),
        then: validation,
    });
}

export const osasvcV2FinalRequest = Koi.object().keys({
    aanvullend_beleid: Koi.object().keys({
        advies_lifestyle: Koi.boolean(),
        advies_lifestyle_namelijk: Koi.when('advies_lifestyle', {
            is: true,
            otherwise: Koi.valid(null),
            then: Koi.string().max(10000).allow(null),
        }),
        anders: Koi.string().max(10000).allow(null),
    }),
    advies_aan_de_huisarts: Koi.string().max(10000).allow(null),
    afwijkendHartritme: getNieuwSlaaponderzoekConditionalValidationForField(Koi.boolean(), true),
    afwijkendHartritmeRemarks: getNieuwSlaaponderzoekConditionalValidationForField(
        Koi.when('afwijkendHartritme', {
            is: true,
            otherwise: Koi.valid(null),
            then: Koi.string().max(1000).allow(null),
        })
    ),
    anamnese: Koi.string().max(10000),
    betreft_proefperiode: Koi.when('therapie_gestart', {
        is: true,
        otherwise: Koi.valid(null),
        then: Koi.boolean(),
    }),
    checkedTreatments: ggzbTherCheckedTreatments,
    conclusie: Koi.string().max(10000),
    cpap_aangepaste_instellingen_namelijk: Koi.when('cpap_instellingen', {
        is: OsasvcV2CpapOption.custom,
        otherwise: Koi.valid(null),
        then: Koi.string().max(10000).allow(null),
    }),
    cpap_instellingen: Koi.when('nieuwe_therapie_starten_namelijk', {
        is: OsasvcV2TherapieNamelijkOption.cpap,
        otherwise: Koi.valid(null),
        then: Koi.koi().enum(OsasvcV2CpapOption),
    }),
    date_contact: Koi.koi().date(),
    emailAddress: emailSchema.allow(null),
    gemiddeldeAhi: getNieuwSlaaponderzoekConditionalValidationForField(Koi.string().max(1000)),
    gemiddeldeOdi: getNieuwSlaaponderzoekConditionalValidationForField(Koi.string().max(1000)),
    gemiddeldeSaturatie: getNieuwSlaaponderzoekConditionalValidationForField(Koi.string().max(100)),
    inefficienteSlaaphygiene: getNieuwSlaaponderzoekConditionalValidationForField(Koi.boolean(), true),
    inefficienteSlaaphygieneRemarks: getNieuwSlaaponderzoekConditionalValidationForField(
        Koi.when('inefficienteSlaaphygiene', {
            is: true,
            otherwise: Koi.valid(null),
            then: Koi.string().max(1000).allow(null),
        })
    ),
    laagstGemetenSaturatie: getNieuwSlaaponderzoekConditionalValidationForField(Koi.string().max(100)),
    nieuw_slaaponderzoek: Koi.boolean(),
    nieuw_slaaponderzoek_end_date: getNieuwSlaaponderzoekConditionalValidationForField(Koi.koi().date().allow(null)),
    nieuw_slaaponderzoek_start_date: getNieuwSlaaponderzoekConditionalValidationForField(Koi.koi().date().allow(null)),
    nieuwe_therapie_starten: Koi.boolean().allow(null),
    nieuwe_therapie_starten_bipap_instellingen: Koi.when('nieuwe_therapie_starten', {
        is: true,
        otherwise: Koi.valid(null),
        then: Koi.string().max(10000).allow(null),
    }),
    nieuwe_therapie_starten_mra_abcd_score: Koi.when('nieuwe_therapie_starten_namelijk', {
        is: OsasvcV2TherapieNamelijkOption.mra,
        otherwise: Koi.valid(null),
        then: Koi.koi().enum(MraAbcdScore),
    }),
    nieuwe_therapie_starten_namelijk: Koi.when('nieuwe_therapie_starten', {
        is: true,
        otherwise: Koi.valid(null),
        then: Koi.koi().enum(OsasvcV2TherapieNamelijkOption),
    }),
    nieuwe_therapie_starten_toelichting: Koi.when('nieuwe_therapie_starten', {
        is: true,
        otherwise: Koi.valid(null),
        then: Koi.string().max(10000).allow(null),
    }),
    odi4: getNieuwSlaaponderzoekConditionalValidationForField(Koi.string().max(100)),
    onderzoek: Koi.string().max(10000).allow(null),
    osasq_resend_date: Koi.koi().date().allow(null),
    osasq_resend_week_dif: Koi.number().min(1).max(100).allow(null),
    overigeRelevanteBevindingenAndersText: getNieuwSlaaponderzoekConditionalValidationForField(
        Koi.string().max(1000).allow(null)
    ),
    positieafhankelijk: getNieuwSlaaponderzoekConditionalValidationForField(
        Koi.string().valid(...positieafhankelijkOptions)
    ),
    positieafhankelijkRemarks: getNieuwSlaaponderzoekConditionalValidationForField(
        Koi.when('positieafhankelijk', {
            is: 'Ja',
            otherwise: Koi.valid(null),
            then: Koi.string().max(1000).allow(null),
        })
    ),
    questionnaires: Koi.array().items(
        Koi.object({
            dateSent: Koi.string(),
            name: Koi.koi().enum(OsasQuestionnaireName),
            score: Koi.number(),
        })
    ),
    relevanteHypoxemie: getNieuwSlaaponderzoekConditionalValidationForField(Koi.boolean()),
    relevanteHypoxemieRemarks: getNieuwSlaaponderzoekConditionalValidationForField(
        Koi.when('relevanteHypoxemie', {
            is: true,
            otherwise: Koi.valid(null),
            then: Koi.string().max(1000).allow(null),
        })
    ),
    slechteSlaapefficientie: getNieuwSlaaponderzoekConditionalValidationForField(Koi.boolean(), true),
    therapie: Koi.when('therapie_gestart', {
        is: true,
        otherwise: Koi.valid(null),
        then: Koi.koi().enum(OsasvcV2CTherapieGestartOption),
    }),
    therapie_gestart: Koi.boolean(),
    therapie_gestart_namelijk: Koi.when('therapie_gestart', {
        is: true,
        otherwise: Koi.valid(null),
        then: Koi.koi().enum(OsasvcV2TherapieNamelijkOption),
    }),
    toelichting: Koi.string().max(10000).allow(null),
    veelBewegingen: getNieuwSlaaponderzoekConditionalValidationForField(Koi.boolean(), true),
    vervolg_afspraak_met_specialist: {
        anders: Koi.string().max(10000).allow(null),
        volgens_protocol: Koi.boolean().allow(null),
    },
    vervolg_afspraak_met_specialist_nieuw_slaaponderzoek: Koi.string().max(10000).allow(null),
});

export const osasvcV2DraftRequest = osasvcV2FinalRequest.concat(
    Koi.object({
        aanvullend_beleid: Koi.object().keys({
            advies_lifestyle: Koi.boolean().allow(null),
            advies_lifestyle_namelijk: Koi.when('advies_lifestyle', {
                is: true,
                otherwise: Koi.valid(null),
                then: Koi.string().max(10000).allow(null),
            }),
            anders: Koi.string().max(10000).allow(null),
        }),
        afwijkendHartritme: getNieuwSlaaponderzoekConditionalValidationForField(Koi.boolean(), true),
        afwijkendHartritmeRemarks: getNieuwSlaaponderzoekConditionalValidationForField(
            Koi.when('afwijkendHartritme', {
                is: true,
                otherwise: Koi.valid(null),
                then: Koi.string().max(1000).allow(null),
            })
        ),
        anamnese: Koi.string().max(10000).allow(null),
        betreft_proefperiode: Koi.when('therapie_gestart', {
            is: true,
            otherwise: Koi.valid(null),
            then: Koi.boolean().allow(null),
        }),
        conclusie: Koi.string().max(10000).allow(null),
        cpap_instellingen: Koi.when('nieuwe_therapie_starten_namelijk', {
            is: OsasvcV2TherapieNamelijkOption.cpap,
            otherwise: Koi.valid(null),
            then: Koi.koi().enum(OsasvcV2CpapOption).allow(null),
        }),
        date_contact: Koi.koi().date().allow(null),
        gemiddeldeAhi: getNieuwSlaaponderzoekConditionalValidationForField(Koi.string().max(1000).allow(null, '')),
        gemiddeldeOdi: getNieuwSlaaponderzoekConditionalValidationForField(Koi.string().max(1000).allow(null, '')),
        gemiddeldeSaturatie: getNieuwSlaaponderzoekConditionalValidationForField(Koi.string().max(100).allow(null, '')),
        inefficienteSlaaphygiene: getNieuwSlaaponderzoekConditionalValidationForField(Koi.boolean().allow(null), true),
        inefficienteSlaaphygieneRemarks: getNieuwSlaaponderzoekConditionalValidationForField(
            Koi.when('inefficienteSlaaphygiene', {
                is: true,
                otherwise: Koi.valid(null),
                then: Koi.string().max(1000).allow(null),
            })
        ),
        laagstGemetenSaturatie: getNieuwSlaaponderzoekConditionalValidationForField(
            Koi.string().max(100).allow(null, '')
        ),
        nieuwe_therapie_starten: Koi.boolean().allow(null),
        nieuwe_therapie_starten_mra_abcd_score: Koi.when('nieuwe_therapie_starten_namelijk', {
            is: OsasvcV2TherapieNamelijkOption.mra,
            otherwise: Koi.valid(null),
            then: Koi.koi().enum(MraAbcdScore).allow(null),
        }),
        nieuwe_therapie_starten_namelijk: Koi.when('nieuwe_therapie_starten', {
            is: true,
            otherwise: Koi.valid(null),
            then: Koi.koi().enum(OsasvcV2TherapieNamelijkOption).allow(null),
        }),
        odi4: getNieuwSlaaponderzoekConditionalValidationForField(Koi.string().max(100).allow(null, '')),
        osasq_resend_date: Koi.koi().date().allow(null),
        osasq_resend_week_dif: Koi.number().allow(null),
        overigeRelevanteBevindingenAndersText: getNieuwSlaaponderzoekConditionalValidationForField(
            Koi.string().max(1000).allow(null)
        ),
        positieafhankelijk: getNieuwSlaaponderzoekConditionalValidationForField(
            Koi.string()
                .valid(...positieafhankelijkOptions)
                .allow(null, '')
        ),
        positieafhankelijkRemarks: getNieuwSlaaponderzoekConditionalValidationForField(
            Koi.when('positieafhankelijk', {
                is: 'Ja',
                otherwise: Koi.valid(null),
                then: Koi.string().max(1000).allow(null),
            })
        ),
        relevanteHypoxemie: getNieuwSlaaponderzoekConditionalValidationForField(Koi.boolean().allow(null)),
        relevanteHypoxemieRemarks: getNieuwSlaaponderzoekConditionalValidationForField(
            Koi.when('relevanteHypoxemie', {
                is: true,
                otherwise: Koi.valid(null),
                then: Koi.string().max(1000).allow(null),
            })
        ),
        slechteSlaapefficientie: getNieuwSlaaponderzoekConditionalValidationForField(Koi.boolean(), true),
        therapie: Koi.when('therapie_gestart', {
            is: true,
            otherwise: Koi.valid(null),
            then: Koi.koi().enum(OsasvcV2CTherapieGestartOption).allow(null),
        }),
        therapie_gestart: Koi.boolean().allow(null),
        therapie_gestart_namelijk: Koi.when('therapie_gestart', {
            is: true,
            otherwise: Koi.valid(null),
            then: Koi.koi().enum(OsasvcV2TherapieNamelijkOption).allow(null),
        }),
        veelBewegingen: getNieuwSlaaponderzoekConditionalValidationForField(Koi.boolean(), true),
    })
);

const sleepExaminationNoteFinalRequestSchema = Koi.object().keys({
    informGeneralPractitioner: Koi.boolean(),
    text: Koi.string().allow(null),
    title: Koi.string().allow(null),
});
const sleepExaminationNoteDraftRequestSchema = sleepExaminationNoteFinalRequestSchema;

export const osasnV1FinalRequest = sleepExaminationNoteFinalRequestSchema;
export const osasnV1DraftRequest = sleepExaminationNoteDraftRequestSchema;

const sleepExaminationAndFollowUpExaminationFinalRequestSchema = Koi.object().keys({
    contactgegevens_patient: Koi.object().keys({
        emailadres: emailSchema,
    }),
    manuallyRequested: Koi.boolean(),
    patientHasSmartphone: Koi.boolean().allow(null),
    shouldCreateOsasq: Koi.boolean(),
    slaaprecorderVerzenden: Koi.valid(SendSleepRecorderOption.sendByKsyos),
    typeSlaaprecorder: Koi.when('slaaprecorderVerzenden', {
        switch: [
            {
                is: SendSleepRecorderOption.sendByKsyos,
                then: Koi.valid(
                    SleepRecorderType.watchpat300,
                    SleepRecorderType.watchpatOne,
                    SleepRecorderType.nightowl
                ),
            },
            { is: null, then: Koi.string().required() },
        ],
    }),
});

export const osasoV1FinalRequest = sleepExaminationAndFollowUpExaminationFinalRequestSchema;
export const osasvoV1FinalRequest = sleepExaminationAndFollowUpExaminationFinalRequestSchema;

const sleepExaminationAndFollowUpExaminationDraftRequestSchema = Koi.object().keys({
    contactgegevens_patient: Koi.object().keys({
        emailadres: emailSchema.allow(null),
    }),
    manuallyRequested: Koi.boolean(),
    patientHasSmartphone: Koi.boolean().allow(null),
    shouldCreateOsasq: Koi.boolean(),
    slaaprecorderVerzenden: Koi.valid(SendSleepRecorderOption.sendByKsyos).allow(null),
    typeSlaaprecorder: Koi.when('slaaprecorderVerzenden', {
        switch: [
            {
                is: SendSleepRecorderOption.sendByKsyos,
                then: Koi.valid(
                    SleepRecorderType.watchpat300,
                    SleepRecorderType.watchpatOne,
                    SleepRecorderType.nightowl
                ).allow(null),
            },
            { is: null, then: Koi.valid(null) },
        ],
    }),
});

export const osasoV1DraftRequest = sleepExaminationAndFollowUpExaminationDraftRequestSchema;
export const osasvoV1DraftRequest = sleepExaminationAndFollowUpExaminationDraftRequestSchema;

const sleepExaminationAndFollowUpExaminationDraftDiagnosticReportSchema = Koi.object().keys({
    beoordeling: Koi.string().max(10000).allow(null),
});

export const osasoV1DiagnosticReport = sleepExaminationAndFollowUpExaminationDraftDiagnosticReportSchema;
export const osasvoV1DiagnosticReport = sleepExaminationAndFollowUpExaminationDraftDiagnosticReportSchema;

export const osasqV1DraftRequest = Koi.object().keys({
    email_address: emailSchema.allow(null),
    resend_date: Koi.koi().date().allow(null),
});

export const osasqV1FinalRequest = Koi.object().keys({
    email_address: emailSchema,
    resend_date: Koi.koi().date(),
});

export const fosq10QuestionnaireFinalSchema = Koi.object().keys({
    difficultyBeingActiveAtNight: Koi.number().min(1).max(4),
    difficultyBeingActiveInTheMorning: Koi.number().min(1).max(4),
    difficultyConcentratingOnWork: Koi.number().min(1).max(4),
    difficultyDrivingAVehicleLongDistances: Koi.number().min(0).max(4),
    difficultyDrivingAVehicleShortDistances: Koi.number().min(0).max(4),
    difficultyRememberingThings: Koi.number().min(1).max(4),
    difficultyVisitingFriendsAndFamily: Koi.number().min(0).max(4),
    difficultyWatchingVideoOrFilm: Koi.number().min(0).max(4),
    haveRelationshipsDeteriorated: Koi.number().min(1).max(4),
    needForIntimacy: Koi.number().min(0).max(4),
});

export const fosq10QuestionnaireDraftSchema = Koi.object().keys({
    difficultyBeingActiveAtNight: Koi.number().min(1).max(4).allow(null),
    difficultyBeingActiveInTheMorning: Koi.number().min(1).max(4).allow(null),
    difficultyConcentratingOnWork: Koi.number().min(1).max(4).allow(null),
    difficultyDrivingAVehicleLongDistances: Koi.number().min(0).max(4).allow(null),
    difficultyDrivingAVehicleShortDistances: Koi.number().min(0).max(4).allow(null),
    difficultyRememberingThings: Koi.number().min(1).max(4).allow(null),
    difficultyVisitingFriendsAndFamily: Koi.number().min(0).max(4).allow(null),
    difficultyWatchingVideoOrFilm: Koi.number().min(0).max(4).allow(null),
    haveRelationshipsDeteriorated: Koi.number().min(1).max(4).allow(null),
    needForIntimacy: Koi.number().min(0).max(4).allow(null),
});

export const essQuestionnaireFinalSchema = Koi.object().keys({
    afterLunch: Koi.number().min(0).max(3),
    inAPublicEnvironment: Koi.number().min(0).max(3),
    restingInTheAfternoon: Koi.number().min(0).max(3),
    sittingInACarAsPassenger: Koi.number().min(0).max(3),
    talkingToSomeone: Koi.number().min(0).max(3),
    waitingInTheCarInTraffic: Koi.number().min(0).max(3),
    whenReading: Koi.number().min(0).max(3),
    whenWatchingTv: Koi.number().min(0).max(3),
});

export const essQuestionnaireDraftSchema = Koi.object().keys({
    afterLunch: Koi.number().min(0).max(3).allow(null),
    inAPublicEnvironment: Koi.number().min(0).max(3).allow(null),
    restingInTheAfternoon: Koi.number().min(0).max(3).allow(null),
    sittingInACarAsPassenger: Koi.number().min(0).max(3).allow(null),
    talkingToSomeone: Koi.number().min(0).max(3).allow(null),
    waitingInTheCarInTraffic: Koi.number().min(0).max(3).allow(null),
    whenReading: Koi.number().min(0).max(3).allow(null),
    whenWatchingTv: Koi.number().min(0).max(3).allow(null),
});

export interface IEssQuestionnaireCustomControlData {
    duringWorkingHours: number | null;
    inAPublicEnvironment: number | null;
    restingInTheAfternoon: number | null;
    sittingInACarAsPassenger: number | null;
    talkingToSomeone: number | null;
    waitingInTheCarInTraffic: number | null;
    whenReading: number | null;
    whenWatchingTv: number | null;
}

export interface IFosq10QuestionnaireCustomControlData {
    difficultyBeingActiveAtNight: number | null;
    difficultyBeingActiveInTheMorning: number | null;
    difficultyConcentratingOnWork: number | null;
    difficultyDrivingAVehicleLongDistances: number | null;
    difficultyDrivingAVehicleShortDistances: number | null;
    difficultyRememberingThings: number | null;
    difficultyVisitingFriendsAndFamily: number | null;
    difficultyWatchingVideoOrFilm: number | null;
    haveRelationshipsDeteriorated: number | null;
    needForIntimacy: number | null;
}

export const osaseV1FinalRequest = Koi.object().keys({
    anamnese: Koi.string().max(10000).allow(null),
    beleid: Koi.string().max(10000).allow(null),
    diagnose: Koi.string().max(10000).allow(null),
    onderzoek: Koi.string().max(10000).allow(null),
});

export const osaseV1DraftRequest = Koi.object().keys({
    anamnese: Koi.string().max(10000).allow(null),
    beleid: Koi.string().max(10000).allow(null),
    diagnose: Koi.string().max(10000).allow(null),
    onderzoek: Koi.string().max(10000).allow(null),
});

export interface IOsaseAsset {
    filename: string;
    id?: string;
    placement: osaseFilePlacement;
    s3Id?: string;
}

export enum osaseFilePlacement {
    leefstijlAttachment = 'leefstijlAttachment',
    mraAttachment = 'mraAttachment',
    papReport = 'papReport',
    sptReport = 'sptReport',
}

export const osasePressureUnitOptions = {
    cmH2O: 'cm H2O',
    hPa: 'hPa',
} as const;

export type osasePressureUnit = (typeof osasePressureUnitOptions)[keyof typeof osasePressureUnitOptions];

export const osasePressureSchema = Koi.object().keys({
    unit: Koi.string()
        .valid(...Object.values(osasePressureUnitOptions))
        .allow(null),
    value: Koi.when('unit', {
        is: null,
        otherwise: Koi.string().max(255),
        then: Koi.valid(null),
    }),
});

export const osaseFile = asset.concat(
    Koi.object({
        placement: Koi.koi().enum(osaseFilePlacement),
    })
);

export const osaseV2FinalRequest = Koi.object().keys({
    anamnese: Koi.string().max(10000).allow(null),
    behandelend_arts_id: Koi.string().uuid().allow(null),
    behandelend_arts_name: Koi.string().allow(null),
    behandeling: Koi.object().keys({
        leefstijl_verandering: Koi.boolean(),
        mra: Koi.boolean(),
        pap: Koi.boolean(),
        spt: Koi.boolean(),
    }),
    beleid: Koi.string().max(10000).allow(null),
    contact_met_patient_gehad: Koi.boolean().allow(null),
    files: Koi.array().items(osaseFile),
    huisarts_op_de_hoogte_brengen: Koi.boolean().allow(null),
    klachtenlast: Koi.koi().enum(OsasComplaintBurden).allow(null),
    leefstijl_volgt_patient_het_beleid: Koi.koi().enum(YesOrNoOrUnknown).allow(null),
    mra_volgt_patient_het_beleid: Koi.koi().enum(YesOrNoOrUnknown).allow(null),
    pap: Koi.object().keys({
        ahi: Koi.string().max(255).allow(null),
        centraal_apneu: Koi.string().max(255).allow(null),
        cheyne_stokes: Koi.string().max(255).allow(null),
        druk: osasePressureSchema,
        druk_epap: osasePressureSchema,
        druk_ipap: osasePressureSchema,
        extra_waarden_uit_rapport: Koi.string().max(10000).allow(null),
        gemiddeld_gebruik: Koi.string().max(255).allow(null),
        maximale_druk: osasePressureSchema,
        minimale_druk: osasePressureSchema,
        nachten_met_ondersteuning: Koi.string().max(255).allow(null),
        ninetyfive_druk: osasePressureSchema,
        obstructief_apneu: Koi.string().max(255).allow(null),
        periode_rapport_end: Koi.koi().date().allow(null),
        periode_rapport_start: Koi.koi().date().allow(null),
        spo2: Koi.string().max(255).allow(null),
        type: Koi.koi().enum(OsasPapType).allow(null),
    }),
    proefperiode: Koi.koi().enum(OsasTrialPeriod).allow(null),
    spt: Koi.object().keys({
        gemiddeld_gebruik: Koi.string().max(255).allow(null),
        nachten_met_ondersteuning: Koi.string().max(255).allow(null),
        periode_rapport_end: Koi.koi().date().allow(null),
        periode_rapport_start: Koi.koi().date().allow(null),
        rugligging_tot: Koi.string().max(255).allow(null),
        rugligging_van: Koi.string().max(255).allow(null),
        toelichting: Koi.string().max(10000).allow(null),
    }),
    toelichting: Koi.string().max(10000).allow(null),
});

export const osaseV2DraftRequest = osaseV2FinalRequest;

export interface IHsdqQuestionnaireCustomControlData {
    annoyingItchy: number | null;
    concerned: number | null;
    confusedHalfAwake: number | null;
    consequencesAfterBadNight: number | null;
    difficultyWakingUp: number | null;
    dryMouth: number | null;
    fallAsleepRepeatedly: number | null;
    fatigueDuringTheDay: number | null;
    functionLessWell: number | null;
    iDontFeelRestedAfterNap: number | null;
    iDontGetEnoughSleep: number | null;
    kickingLegMovements: number | null;
    lieAwake: number | null;
    moreThanTenHours: number | null;
    moveLegs: number | null;
    nightmares: number | null;
    normalTime: number | null;
    poorSleep: number | null;
    restlessLegs: number | null;
    screaming: number | null;
    sleepAttacks: number | null;
    sleepBadlyDuringDay: number | null;
    sleepWalking: number | null;
    snoreLoudly: number | null;
    sometimesInjured: number | null;
    stopBreathing: number | null;
    suffocation: number | null;
    timeVaries: number | null;
    troublesFallingAsleep: number | null;
    unintentionallyFallAsleepDuringDay: number | null;
    urgeToMoveLegs: number | null;
    vividDream: number | null;
}

export const hsdqQuestionnaireDraftSchema = Koi.object().keys({
    annoyingItchy: Koi.number().min(1).max(5).allow(null),
    concerned: Koi.number().min(1).max(5).allow(null),
    confusedHalfAwake: Koi.number().min(1).max(5).allow(null),
    consequencesAfterBadNight: Koi.number().min(1).max(5).allow(null),
    difficultyWakingUp: Koi.number().min(1).max(5).allow(null),
    dryMouth: Koi.number().min(1).max(5).allow(null),
    fallAsleepRepeatedly: Koi.number().min(1).max(5).allow(null),
    fatigueDuringTheDay: Koi.number().min(1).max(5).allow(null),
    functionLessWell: Koi.number().min(1).max(5).allow(null),
    iDontFeelRestedAfterNap: Koi.number().min(1).max(5).allow(null),
    iDontGetEnoughSleep: Koi.number().min(1).max(5).allow(null),
    kickingLegMovements: Koi.number().min(1).max(5).allow(null),
    lieAwake: Koi.number().min(1).max(5).allow(null),
    moreThanTenHours: Koi.number().min(1).max(5).allow(null),
    moveLegs: Koi.number().min(1).max(5).allow(null),
    nightmares: Koi.number().min(1).max(5).allow(null),
    normalTime: Koi.number().min(1).max(5).allow(null),
    poorSleep: Koi.number().min(1).max(5).allow(null),
    restlessLegs: Koi.number().min(1).max(5).allow(null),
    screaming: Koi.number().min(1).max(5).allow(null),
    sleepAttacks: Koi.number().min(1).max(5).allow(null),
    sleepBadlyDuringDay: Koi.number().min(1).max(5).allow(null),
    sleepWalking: Koi.number().min(1).max(5).allow(null),
    snoreLoudly: Koi.number().min(1).max(5).allow(null),
    sometimesInjured: Koi.number().min(1).max(5).allow(null),
    stopBreathing: Koi.number().min(1).max(5).allow(null),
    suffocation: Koi.number().min(1).max(5).allow(null),
    timeVaries: Koi.number().min(1).max(5).allow(null),
    troublesFallingAsleep: Koi.number().min(1).max(5).allow(null),
    unintentionallyFallAsleepDuringDay: Koi.number().min(1).max(5).allow(null),
    urgeToMoveLegs: Koi.number().min(1).max(5).allow(null),
    vividDream: Koi.number().min(1).max(5).allow(null),
});

export const hsdqQuestionnaireFinalSchema = Koi.object().keys({
    annoyingItchy: Koi.number().min(1).max(5),
    concerned: Koi.number().min(1).max(5),
    confusedHalfAwake: Koi.number().min(1).max(5),
    consequencesAfterBadNight: Koi.number().min(1).max(5),
    difficultyWakingUp: Koi.number().min(1).max(5),
    dryMouth: Koi.number().min(1).max(5),
    fallAsleepRepeatedly: Koi.number().min(1).max(5),
    fatigueDuringTheDay: Koi.number().min(1).max(5),
    functionLessWell: Koi.number().min(1).max(5),
    iDontFeelRestedAfterNap: Koi.number().min(1).max(5),
    iDontGetEnoughSleep: Koi.number().min(1).max(5),
    kickingLegMovements: Koi.number().min(1).max(5),
    lieAwake: Koi.number().min(1).max(5),
    moreThanTenHours: Koi.number().min(1).max(5),
    moveLegs: Koi.number().min(1).max(5),
    nightmares: Koi.number().min(1).max(5),
    normalTime: Koi.number().min(1).max(5),
    poorSleep: Koi.number().min(1).max(5),
    restlessLegs: Koi.number().min(1).max(5),
    screaming: Koi.number().min(1).max(5),
    sleepAttacks: Koi.number().min(1).max(5),
    sleepBadlyDuringDay: Koi.number().min(1).max(5),
    sleepWalking: Koi.number().min(1).max(5),
    snoreLoudly: Koi.number().min(1).max(5),
    sometimesInjured: Koi.number().min(1).max(5),
    stopBreathing: Koi.number().min(1).max(5),
    suffocation: Koi.number().min(1).max(5),
    timeVaries: Koi.number().min(1).max(5),
    troublesFallingAsleep: Koi.number().min(1).max(5),
    unintentionallyFallAsleepDuringDay: Koi.number().min(1).max(5),
    urgeToMoveLegs: Koi.number().min(1).max(5),
    vividDream: Koi.number().min(1).max(5),
});

export function getSaveOsasoQuestionnaireValidationSchema(validationType: SleepQuestionnairesValidationType) {
    let questionnaireSchema;
    switch (validationType) {
        case SleepQuestionnairesValidationType.essDraft:
            questionnaireSchema = Koi.object({
                answer: essQuestionnaireDraftSchema,
                name: Koi.string().valid(OsasQuestionnaireName.ess),
            });
            break;
        case SleepQuestionnairesValidationType.essFinal:
            questionnaireSchema = Koi.object({
                answer: essQuestionnaireFinalSchema,
                name: Koi.string().valid(OsasQuestionnaireName.ess),
            });
            break;
        case SleepQuestionnairesValidationType.fosq10Draft:
            questionnaireSchema = Koi.object({
                answer: fosq10QuestionnaireDraftSchema,
                name: Koi.string().valid(OsasQuestionnaireName.fosq10),
            });
            break;
        case SleepQuestionnairesValidationType.fosq10Final:
            questionnaireSchema = Koi.object({
                answer: fosq10QuestionnaireFinalSchema,
                name: Koi.string().valid(OsasQuestionnaireName.fosq10),
            });
            break;
        case SleepQuestionnairesValidationType.hsdqDraft:
            questionnaireSchema = Koi.object({
                answer: hsdqQuestionnaireDraftSchema,
                name: Koi.string().valid(OsasQuestionnaireName.hsdq),
            });
            break;
        case SleepQuestionnairesValidationType.hsdqFinal:
            questionnaireSchema = Koi.object({
                answer: hsdqQuestionnaireFinalSchema,
                name: Koi.string().valid(OsasQuestionnaireName.hsdq),
            });
            break;
        default:
            throw new Error('Unable to find validation schema');
    }

    return Koi.object({
        procedureId: Koi.string().uuid(),
        questionnaire: questionnaireSchema,
        validationType: Koi.koi().enum(SleepQuestionnairesValidationType),
    }).options({ presence: 'required' });
}
